/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W3 (Wave-0 infra) — useRatingDispatch (§31/§32).
 *
 * The SHARED abortable vendor-dispatch hook every vendor selector (fan/EW/PHX/
 * coil) reuses. Wraps the postRating mutation (ratingApi) with:
 *   - cancel-on-resubmit: keep the last mutation promise (promiseRef) and
 *     .abort() it before re-firing — the legacy `self.rate_request.abort()`
 *     (data_exchanger §31). RTKQ mutation triggers return a promise with an
 *     .abort() method (AbortController); a superseded dispatch is aborted, and
 *     the aborted promise rejects with `name === 'AbortError'` which we map to a
 *     `{cancelled:true}` result (legacy data_exchanger resolves `{cancelled}`).
 *   - abort on unmount (data-safety: no dangling vendor round-trip).
 *
 * ★ DATA-SAFETY: `dispatchRating` fires an EXTERNAL vendor call. The selector
 * MUST gate it behind a userArmed Rate click — this hook does NOT auto-fire.
 * The read-only SMOKE never calls dispatchRating ⇒ 0 /rating/ calls.
 *
 * Returns the §32 always-200 body (ok/errors/warnings/models/notes/version) —
 * the selector reads the body, never the HTTP status. On a vendor/permission
 * 400 (malformed envelope) the mutation rejects; we surface that as a result
 * with ok:false so the selector's failure render path runs uniformly.
 */

import { useCallback, useEffect, useRef } from 'react';
import { usePostRatingMutation } from './ratingApi';
import type { RatingEnvelope, RatingResponse } from './ratingApi';
import { analyticsComponentName, track } from 'app/analytics';

/** component_rated, once per finished vendor rating the user asked for. Not
 * counted:
 *  - a superseded or cancelled rating;
 *  - Fan's curve fetch (a second /rating/ call after a rating, for the curve
 *    vendors). Its `function_name` can't tell it apart, since Greenheck's real
 *    rating uses the same name as Rosenberg's curve call;
 *  - the header popup's automatic re-rates (restoring a saved entry, a
 *    manufacturer switch; fan/coil/energy wheel/PHX `autoRateRef`).
 * The last two opt out at the call site with `dispatchRating(envelope,
 * { track: false })`. */
function trackRating(
  envelope: RatingEnvelope,
  body: RatingResponse,
  startedAt: number,
  source: 'board' | 'header'
): void {
  if (body.cancelled) return;
  const results = Array.isArray(body.models) ? body.models.length : 0;
  track('component_rated', {
    component: analyticsComponentName(envelope.part),
    // A catalog slug from ratingEnvelope's MFG maps (or the CMS manufacturer
    // name, lower-cased, for coils); never anything the user typed.
    manufacturer: envelope.manufacturer,
    success: body.ok === true && results > 0,
    results,
    duration_ms: Date.now() - startedAt,
    source,
  });
}

export interface RatingDispatchHandle {
  /** Fire the vendor rate (aborts any in-flight one first). Resolves the §32
   * body, or `{cancelled:true}` when superseded/aborted. NEVER call on mount. */
  dispatchRating: (
    envelope: RatingEnvelope,
    opts?: { track?: boolean }
  ) => Promise<RatingResponse>;
  /** Abort the in-flight rate without re-firing (cancel button / unmount). */
  abortRating: () => void;
  /** True while a vendor rate is in flight. */
  isRating: boolean;
}

/** `source` says where the selector is open: on the AHU board, or as the
 * header's independent rating popup (`headerMode`). Analytics only. */
export function useRatingDispatch(source: 'board' | 'header' = 'board'): RatingDispatchHandle {
  const [postRating, { isLoading: isRating }] = usePostRatingMutation();
  // the last mutation trigger promise (carries .abort()).
  const promiseRef = useRef<{ abort: () => void } | null>(null);

  const abortRating = useCallback(() => {
    promiseRef.current?.abort?.();
    promiseRef.current = null;
  }, []);

  const dispatchRating = useCallback(
    async (envelope: RatingEnvelope, opts?: { track?: boolean }): Promise<RatingResponse> => {
      // cancel-on-resubmit (legacy self.rate_request.abort()).
      promiseRef.current?.abort?.();
      const promise = postRating(envelope);
      promiseRef.current = promise as unknown as { abort: () => void };
      const startedAt = Date.now();
      const finish = (body: RatingResponse): RatingResponse => {
        if (opts?.track !== false) trackRating(envelope, body, startedAt, source);
        return body;
      };
      try {
        const body = await promise.unwrap();
        return finish(body as RatingResponse);
      } catch (err: any) {
        // RTKQ surfaces an aborted trigger as { name: 'AbortError' } or
        // { error: { name: 'AbortError' } } — map to the legacy cancelled body.
        const name = err?.name ?? err?.error?.name;
        if (name === 'AbortError') return { cancelled: true };
        // A real vendor/permission 400 (malformed envelope) — surface ok:false
        // with the error payload so the selector's failure render runs.
        const data = err?.data as RatingResponse | undefined;
        if (data && typeof data === 'object') return finish({ ok: false, ...data });
        return finish({ ok: false, errors: [String(err?.error ?? err?.message ?? err)] });
      } finally {
        if (promiseRef.current === (promise as unknown)) promiseRef.current = null;
      }
    },
    [postRating, source]
  );

  // abort on unmount (data-safety).
  useEffect(() => () => promiseRef.current?.abort?.(), []);

  return { dispatchRating, abortRating, isRating };
}
